import React, { useCallback, useEffect, useMemo, useState } from "react";
import AppShell from "../components/AppShell";
import { Button } from "../components/ui/button";
import { ArrowsClockwise, Info } from "@phosphor-icons/react";
import { toast } from "sonner";
import { getFdrHeatmap } from "../lib/api";

// ---------------------------------------------------------------------------
// FDR colour — standard palette by absolute difficulty (1 = easiest, 5 = hardest):
//   ≤ 2.4  green (easy run)
//   2.4–3.4 neutral (no colour)
//   ≥ 3.4  red (hard run)
// Continuous interpolation inside each band for phase averages.
// ---------------------------------------------------------------------------
const GREEN = [46, 158, 91];    // #2E9E5B
const LIGHT_GREEN = [158, 232, 180]; // #9EE8B4
const NEUTRAL = [26, 26, 36];   // near-bg
const LIGHT_RED = [240, 121, 105]; // #F07969
const RED = [200, 44, 34];      // #C82C22

function lerp(a, b, t) {
    return a.map((x, i) => Math.round(x + (b[i] - x) * t));
}
function rgb(c) { return `rgb(${c[0]}, ${c[1]}, ${c[2]})`; }

function fdrColour(difficulty, isBlank = false) {
    if (isBlank || difficulty == null) return { bg: "#14101f", text: "#555" };
    const d = Math.max(1, Math.min(5, difficulty));
    if (d <= 2.0) return { bg: rgb(GREEN), text: "#fff" };
    if (d <= 2.6) {
        const t = (d - 2.0) / 0.6;
        return { bg: rgb(lerp(GREEN, LIGHT_GREEN, t)), text: t > 0.5 ? "#0a0a0a" : "#fff" };
    }
    if (d < 3.0) {
        const t = (d - 2.6) / 0.4;
        return { bg: rgb(lerp(LIGHT_GREEN, NEUTRAL, t)), text: t > 0.5 ? "#ccc" : "#0a0a0a" };
    }
    if (d <= 3.4) return { bg: "rgba(255,255,255,0.04)", text: "#bbb" };
    if (d < 4.2) {
        const t = (d - 3.4) / 0.8;
        return { bg: rgb(lerp(NEUTRAL, LIGHT_RED, t)), text: t > 0.5 ? "#0a0a0a" : "#ccc" };
    }
    const t = Math.min(1, (d - 4.2) / 0.8);
    return { bg: rgb(lerp(LIGHT_RED, RED, t)), text: "#fff" };
}

// Text colour for standalone numbers (avg FDR column)
function fdrTextColour(d) {
    if (d == null) return "#777";
    if (d <= 2.6) return rgb(LIGHT_GREEN);
    if (d >= 3.4) return rgb(LIGHT_RED);
    return "rgba(255,255,255,0.7)";
}

// Ease score (0-1) → equivalent difficulty 1-5 for colouring

const PHASE_LABELS = {
    gw1_6: "Start",
    gw7_12: "První třetina",
    gw13_18: "Před Vánoci",
    gw19_24: "Vánoce",
    gw25_30: "Před finišem",
    gw31_38: "Run-in",
};

const WINDOWS = [4, 6, 8, 10, 38];

const VIEWS = [
    ["official", "Oficiální", "Oficiální FPL FDR — celková síla soupeře"],
    ["attack", "Útok · MID/FWD", "Jak snadné je proti soupeři SKÓROVAT — hodnotí obranu soupeře (xGA ze SportMonks, fallback FPL síly). Pro výběr záložníků a útočníků."],
    ["defence", "Obrana · GK/DEF", "Jak těžké je proti soupeři UDRŽET NULU — hodnotí útok soupeře (xG ze SportMonks, fallback FPL síly). Pro výběr brankářů a obránců."],
];

const VIEW_DESC = {
    official: null,
    attack: "Pohled pro útočníky: obtížnost = kvalita OBRANY soupeře (xGA). Zelená = soupeř hodně inkasuje → dobrý zápas pro tvoje MID/FWD.",
    defence: "Pohled pro obránce: obtížnost = kvalita ÚTOKU soupeře (xG). Zelená = soupeř málo střílí → dobrá šance na clean sheet pro GK/DEF.",
};

export default function FdrHeatmap() {
    const [windowSize, setWindowSize] = useState(6);
    const [mode, setMode] = useState("gw");
    const [view, setView] = useState("official");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [sortCol, setSortCol] = useState(null);   // null = default (ease), "gw:5" or "phase:2"
    const [sortDir, setSortDir] = useState("asc");  // asc = easiest first
    const [expanded, setExpanded] = useState(null); // "TeamName:phaseKey"

    const load = useCallback(async (w, m, v) => {
        setLoading(true);
        try {
            const d = await getFdrHeatmap(w, undefined, m, v);
            setData(d);
        } catch (e) {
            toast.error("Nepodařilo se načíst FDR heatmapu");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { setSortCol(null); setExpanded(null); load(windowSize, mode, view); }, [windowSize, mode, view, load]);

    const summary = data?.summary;
    const isPhases = data?.mode === "phases";

    const sortedRows = useMemo(() => {
        const rows = data?.rows || [];
        if (!sortCol) return rows;
        const [kind, idxStr] = sortCol.split(":");
        const getVal = (row) => {
            let cell;
            if (kind === "phase") cell = row.cells[parseInt(idxStr, 10)];
            else cell = row.cells.find((c) => c.gw === parseInt(idxStr, 10));
            if (!cell || cell.is_blank || cell.difficulty == null) return Infinity;
            return cell.difficulty;
        };
        return [...rows].sort((a, b) => {
            const va = getVal(a), vb = getVal(b);
            if (va === vb) return 0;
            const cmp = va < vb ? -1 : 1;
            return sortDir === "asc" ? cmp : -cmp;
        });
    }, [data, sortCol, sortDir]);

    const toggleSort = (key) => {
        if (sortCol === key) {
            if (sortDir === "asc") setSortDir("desc");
            else { setSortCol(null); setSortDir("asc"); }
        } else {
            setSortCol(key);
            setSortDir("asc");
        }
    };
    const indicator = (key) => sortCol === key ? (sortDir === "asc" ? " ↑" : " ↓") : "";

    const top = sortedRows[0];
    const bottom = sortedRows[sortedRows.length - 1];

    return (
        <AppShell>
            <div className="space-y-6" data-testid="fdr-page">
                <div className="flex items-end justify-between flex-wrap gap-4">
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-widest text-fa-cream">
                            Planning · Fixture Difficulty
                        </div>
                        <h1 className="font-display font-black text-3xl uppercase tracking-tight mt-1 bg-gradient-to-r from-fa-cream via-white to-fa-mint bg-clip-text text-transparent">
                            FDR Heatmap
                        </h1>
                        <p className="text-white/50 text-sm mt-1 max-w-2xl">
                            Obtížnost každého zápasu = <strong className="text-white/80">síla soupeře podle FPL</strong> na
                            škále <strong className="text-fa-mint">1 (nejlehčí)</strong> až <strong className="text-red-300">5 (nejtěžší)</strong>.
                            Buňka fáze = průměr obtížností všech zápasů v dané fázi. Klikni na hlavičku sloupce pro seřazení.
                        </p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-[10px] uppercase tracking-widest text-white/50">Okno</span>
                        {WINDOWS.map((w) => (
                            <button
                                key={w}
                                type="button"
                                onClick={() => { setMode("gw"); setWindowSize(w); }}
                                data-testid={`fdr-window-${w}`}
                                className={`font-mono text-xs uppercase tracking-widest px-3 py-1.5 border transition-colors ${mode === "gw" && w === windowSize
                                    ? "bg-fa-cream text-black border-fa-cream"
                                    : "border-white/20 hover:border-fa-cream/50 text-white/70"
                                    }`}
                            >
                                {w === 38 ? "Celá sezóna" : `${w} GW`}
                            </button>
                        ))}
                        <button
                            type="button"
                            onClick={() => setMode("phases")}
                            data-testid="fdr-window-phases"
                            title="6 fází sezóny: Start, První třetina, Před Vánoci, Vánoce, Před finišem, Run-in"
                            className={`font-mono text-xs uppercase tracking-widest px-3 py-1.5 border transition-colors ${mode === "phases"
                                ? "bg-fa-mint text-black border-fa-mint"
                                : "border-white/20 hover:border-fa-mint/50 text-white/70"
                                }`}
                        >
                            Fáze
                        </button>
                        <Button
                            onClick={() => load(windowSize, mode, view)}
                            disabled={loading}
                            data-testid="fdr-refresh"
                            variant="outline"
                            className="bg-transparent border-white/20 hover:bg-white/5 rounded-none font-mono text-xs uppercase h-8"
                        >
                            <ArrowsClockwise size={12} weight="bold" className={loading ? "animate-spin" : ""} />
                        </Button>
                    </div>
                </div>

                {/* Position view toggle — official / attack / defence FDR */}
                <div className="flex items-center gap-2 flex-wrap" data-testid="fdr-view-toggle">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-white/50">FDR pohled</span>
                    {VIEWS.map(([k, label, tip]) => (
                        <button
                            key={k}
                            type="button"
                            onClick={() => setView(k)}
                            data-testid={`fdr-view-${k}`}
                            title={tip}
                            className={`font-mono text-xs uppercase tracking-widest px-3 py-1.5 border transition-colors ${view === k
                                ? "bg-fa-sky text-black border-fa-sky font-bold"
                                : "border-white/20 hover:border-fa-sky/50 text-white/70"
                                }`}
                        >
                            {label}
                        </button>
                    ))}
                    {view !== "official" && summary?.xg_teams > 0 && (
                        <span className="font-mono text-[9px] text-fa-mint/70" data-testid="fdr-xg-note">
                            xG data: SportMonks ({summary.xg_teams} týmů)
                        </span>
                    )}
                </div>
                {VIEW_DESC[view] && (
                    <p className="font-mono text-[10px] text-fa-sky/90 border-l-2 border-fa-sky pl-2 max-w-3xl" data-testid="fdr-view-desc">
                        {VIEW_DESC[view]}
                    </p>
                )}

                {/* Summary cards */}
                {summary && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="fdr-summary">
                        <SummaryCard
                            label="Rozsah"
                            value={`GW${summary.start_gw}–${summary.end_gw}`}
                        />
                        <SummaryCard
                            label="Nejlehčí los"
                            value={top ? top.team.short_name : "—"}
                            sub={top ? `Ø FDR ${top.avg_difficulty}` : ""}
                            color="#9EE8B4"
                        />
                        <SummaryCard
                            label="Nejtěžší los"
                            value={bottom ? bottom.team.short_name : "—"}
                            sub={bottom ? `Ø FDR ${bottom.avg_difficulty}` : ""}
                            color="#FF6B5E"
                        />
                        <SummaryCard
                            label="Blanks · Doubles"
                            value={`${summary.blanks} · ${summary.doubles}`}
                            sub="v rozsahu"
                        />
                    </div>
                )}

                {/* Heatmap grid */}
                {data && (
                    <div className="border border-white/10 bg-black/30 overflow-x-auto" data-testid="fdr-grid">
                        <table className="min-w-full text-sm">
                            <thead>
                                <tr className="bg-white/5 border-b border-white/10 text-left">
                                    <th className="sticky left-0 bg-[#1a1332] font-mono text-[10px] uppercase tracking-widest text-white/50 px-3 py-2 z-10">Team</th>
                                    {isPhases ? (
                                        (data.phases || []).map((ph, i) => (
                                            <th
                                                key={ph.key}
                                                onClick={() => toggleSort(`phase:${i}`)}
                                                data-testid={`fdr-col-${ph.key}`}
                                                title={`Seřadit dle ${PHASE_LABELS[ph.key] || ph.key}`}
                                                className={`font-mono text-[10px] uppercase tracking-widest px-3 py-2 text-center cursor-pointer hover:text-white ${sortCol === `phase:${i}` ? "text-fa-mint" : "text-white/50"}`}
                                            >
                                                {PHASE_LABELS[ph.key] || ph.key}{indicator(`phase:${i}`)}
                                                <div className="text-[8px] text-white/30 normal-case">GW{ph.gws[0]}–{ph.gws[1]}</div>
                                            </th>
                                        ))
                                    ) : (
                                        data.gws.map((gw) => (
                                            <th
                                                key={gw}
                                                onClick={() => toggleSort(`gw:${gw}`)}
                                                data-testid={`fdr-col-gw${gw}`}
                                                title={`Seřadit dle GW${gw}`}
                                                className={`font-mono text-[10px] uppercase tracking-widest px-2 py-2 text-center cursor-pointer hover:text-white ${sortCol === `gw:${gw}` ? "text-fa-mint" : "text-white/50"}`}
                                            >
                                                {gw}{indicator(`gw:${gw}`)}
                                            </th>
                                        ))
                                    )}
                                    <th className="font-mono text-[10px] uppercase tracking-widest text-white/50 px-3 py-2 text-right">Ø FDR</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedRows.map((row) => (
                                    <HeatRow
                                        key={row.team.id ?? row.team.name}
                                        row={row}
                                        isPhases={isPhases}
                                        expanded={expanded}
                                        onPhaseClick={(phaseKey) => {
                                            const k = `${row.team.name}:${phaseKey}`;
                                            setExpanded((cur) => (cur === k ? null : k));
                                        }}
                                        colCount={(isPhases ? (data.phases || []).length : data.gws.length) + 2}
                                    />
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Legend */}
                <div className="flex items-center gap-4 flex-wrap font-mono text-[10px] uppercase tracking-widest text-white/50">
                    <Info size={12} weight="bold" className="text-fa-cream" />
                    <span>FDR 1 = nejlehčí · 5 = nejtěžší:</span>
                    <LegendChip colour={rgb(GREEN)} label="≤ 2.4 lehký" />
                    <LegendChip colour="rgba(255,255,255,0.10)" label="2.4–3.4 průměr" />
                    <LegendChip colour={rgb(RED)} label="≥ 3.4 těžký" />
                    <span className="text-white/30">·</span>
                    <span className="text-fa-cream">H</span>
                    <span className="text-white/40">= doma</span>
                    <span className="text-fa-cream">A</span>
                    <span className="text-white/40">= venku</span>
                    <span className="text-white/30">·</span>
                    <span className="text-fa-cream">DGW/BGW</span>
                    <span className="text-white/40">= dvojitý / prázdný GW</span>
                </div>
            </div>
        </AppShell>
    );
}

function HeatRow({ row, isPhases, expanded, onPhaseClick, colCount }) {
    const expandedCell = isPhases
        ? row.cells.find((c) => expanded === `${row.team.name}:${c.phase}`)
        : null;
    return (
        <>
        <tr className="border-b border-white/5" data-testid={`fdr-row-${row.team.short_name}`}>
            <td className="sticky left-0 bg-[#1a1332] px-3 py-2 font-display font-bold text-sm z-10">
                {row.team.short_name}
                <div className="font-mono text-[9px] text-white/30">{row.team.name}</div>
            </td>
            {isPhases
                ? row.cells.map((cell) => (
                    <PhaseHeatCell
                        key={cell.phase}
                        cell={cell}
                        active={expanded === `${row.team.name}:${cell.phase}`}
                        onClick={() => onPhaseClick(cell.phase)}
                    />
                ))
                : row.cells.map((cell) => <HeatCell key={cell.gw} cell={cell} />)}
            <td className="px-3 py-2 text-right font-mono text-xs">
                <span style={{ color: fdrTextColour(row.avg_difficulty) }}>
                    {row.avg_difficulty ?? "—"}
                </span>
            </td>
        </tr>
        {expandedCell && (
            <tr className="bg-black/50 border-b border-white/10" data-testid={`fdr-phase-detail-${row.team.short_name}`}>
                <td colSpan={colCount} className="px-3 py-2.5">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-[9px] uppercase tracking-widest text-white/45">
                            {row.team.short_name} · {PHASE_LABELS[expandedCell.phase]} (GW{expandedCell.gw_range[0]}–{expandedCell.gw_range[1]}):
                        </span>
                        {(expandedCell.matches || []).map((m) => {
                            const mc = fdrColour(m.difficulty);
                            return (
                                <span
                                    key={`${m.gw}-${m.opponent_short}`}
                                    className="inline-flex items-center gap-1.5 border border-black/40 px-2 py-1 font-mono text-[10px]"
                                    style={{ backgroundColor: mc.bg, color: mc.text }}
                                >
                                    <span className="opacity-75">GW{m.gw}</span>
                                    <span className="font-bold">{m.home ? m.opponent_short : m.opponent_short.toLowerCase()}</span>
                                    <span className="opacity-75">{m.home ? "H" : "A"} · {m.difficulty}</span>
                                </span>
                            );
                        })}
                    </div>
                </td>
            </tr>
        )}
        </>
    );
}

function HeatCell({ cell }) {
    const c = fdrColour(cell.difficulty, cell.is_blank);
    return (
        <td className="px-0.5 py-0.5">
            <div
                className="relative flex flex-col items-center justify-center min-w-[56px] h-12 border border-black/30 transition-transform hover:scale-105"
                style={{ backgroundColor: c.bg, color: c.text }}
                data-testid={`fdr-cell-gw${cell.gw}`}
                title={cell.is_blank ? "Blank GW" : `FDR ${cell.difficulty} · ${cell.opponent_short} ${cell.home ? "(doma)" : "(venku)"}`}
            >
                {cell.is_blank ? (
                    <>
                        <span className="font-mono text-[10px] opacity-80">—</span>
                        <span className="font-mono text-[8px] uppercase tracking-widest">BGW</span>
                    </>
                ) : (
                    <>
                        <span className="font-display font-bold text-[13px]">
                            {cell.home ? cell.opponent_short : cell.opponent_short.toLowerCase()}
                        </span>
                        <span className="font-mono text-[9px] uppercase tracking-widest opacity-90">
                            {cell.difficulty} {cell.home ? "H" : "A"}{cell.is_double ? " · DGW" : ""}
                        </span>
                    </>
                )}
            </div>
        </td>
    );
}

function PhaseHeatCell({ cell, active, onClick }) {
    const c = fdrColour(cell.difficulty, cell.is_blank);
    return (
        <td className="px-0.5 py-0.5">
            <div
                onClick={cell.is_blank ? undefined : onClick}
                className={`relative flex flex-col items-center justify-center min-w-[92px] h-14 border transition-transform hover:scale-105 ${cell.is_blank ? "border-black/30" : "cursor-pointer"} ${active ? "border-fa-cream ring-1 ring-fa-cream" : "border-black/30"}`}
                style={{ backgroundColor: c.bg, color: c.text }}
                data-testid={`fdr-phase-cell-${cell.phase}`}
                title={cell.is_blank ? "Bez zápasů" : `Ø FDR ${cell.difficulty} · klikni pro zápasy`}
            >
                {cell.is_blank ? (
                    <span className="font-mono text-[10px] opacity-80">—</span>
                ) : (
                    <>
                        <span className="font-display font-bold text-base">{cell.difficulty?.toFixed(2)}</span>
                        <span className="font-mono text-[9px] uppercase tracking-widest opacity-90">
                            {cell.fixtures} záp. · {cell.home}× doma
                        </span>
                    </>
                )}
            </div>
        </td>
    );
}

function SummaryCard({ label, value, sub, color }) {
    return (
        <div className="border border-white/10 bg-black/40 p-3">
            <div className="font-mono text-[9px] uppercase tracking-widest text-white/40">{label}</div>
            <div
                className="font-display font-black text-xl mt-1"
                style={color ? { color } : undefined}
            >
                {value}
            </div>
            {sub && <div className="font-mono text-[10px] text-white/40 mt-0.5">{sub}</div>}
        </div>
    );
}

function LegendChip({ colour, label }) {
    return (
        <span className="inline-flex items-center gap-1.5">
            <span
                className="inline-block w-3 h-3"
                style={{ backgroundColor: colour }}
            />
            <span>{label}</span>
        </span>
    );
}
